<script setup lang="ts">
import type { Product } from "mws-ts-v2-sdk";

const { gridAmount = 4, searchTerm } = defineProps<{
  searchTerm: string;
  gridAmount?: 2 | 4;
  isMobile?: boolean;
}>();

type Emits = {
  (e: "enter-search-result", searchTerm: SearchHistoryItem): void;
  (e: "close-search-bar"): void;
};

const emit = defineEmits<Emits>();

// User can navigate the UI when the input has 2 or more characters
const searchInterfaceEnabled = computed(() => searchTerm.length >= 2);

const { products, showNoResultsPlaceholder } = useSearchProductPreview(
  () => searchTerm,
  searchInterfaceEnabled,
);

const { searchItemClicked } = useAnalytics();
const onQuickSearchItemClicked = (product: Product) => {
  searchItemClicked({
    search_term: searchTerm,
    tab_origin: "quickSearch",
    name: product.name,
    type: "product",
  });

  emit("close-search-bar");
};

const { searchHistory } = useSearchHistory();

const showRecentSearches = computed(() => {
  return searchHistory.value.length > 0 && !searchInterfaceEnabled.value;
});

const showDropDown = computed(
  () => showRecentSearches.value || searchInterfaceEnabled.value,
);

const { suggestions } = useSearchSuggestions(
  () => searchTerm,
  searchInterfaceEnabled,
);

const exactMatchSuggestion = computed(() => {
  if (!suggestions.value) return null;

  return (
    suggestions.value.find((suggestion) => suggestion.isExactMatch) || null
  );
});

defineExpose({ exactMatchSuggestion });
</script>

<template>
  <div
    class="lg:fixed lg:z-40 lg:top-0 lg:border-b lg:h-full lg:w-full lg:left-0 lg:mt-24"
  >
    <button
      :data-cy="`${isMobile ? 'mobile' : 'desktop'}-search-modal-overlay`"
      class="hidden lg:block lg:absolute lg:top-0 lg:left-0 lg:w-full lg:h-full lg:cursor-pointer lg:bg-black/50"
      @click="$emit('close-search-bar')"
    />
    <div
      :class="{ 'lg:hidden': !showDropDown }"
      class="quick-search-results lg:min-h-112 lg:bg-platform-bg-1 lg:py-6 lg:block-container lg:relative"
    >
      <div class="lg:site-viewport-container">
        <div class="lg:flex">
          <div class="lg:w-1/4 text-platform-text-1">
            <SearchModalRecentSearches
              v-if="showRecentSearches"
              @enter-search-result="$emit('enter-search-result', $event)"
            />
            <SearchModalSuggestions
              v-if="searchInterfaceEnabled && suggestions"
              data-test="quick-search-suggestions"
              :is-mobile="isMobile"
              :debounced-search-term="searchTerm"
              :search-term="searchTerm"
              :search-suggestions="suggestions"
              @close-search-bar="$emit('close-search-bar')"
              @enter-search-result="$emit('enter-search-result', $event)"
            />
          </div>

          <!-- v-show is needed for the animation to work on initial load -->
          <div
            v-show="searchInterfaceEnabled"
            class="lg:w-3/4 lg:ml-4"
          >
            <BaseLoadingSpinner v-if="!products" />
            <LazyBasePlaceholderNoResults
              v-else-if="showNoResultsPlaceholder"
              variant="searchPage"
              class="justify-center"
            />
            <SearchModalProductResults
              v-else
              :is-mobile="isMobile"
              :products="products?.results"
              :grid-amount="gridAmount"
              class="mt-4 lg:mt-0"
              data-test="quick-search-products"
              @product-clicked="onQuickSearchItemClicked"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.quick-search-results {
  animation: searchModalEnter 0.2s ease-out forwards;
  animation-delay: 0.05s;
  opacity: 0;
}

@keyframes searchModalEnter {
  0% {
    opacity: 0;
    transform: translateY(-5%);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
